import { getDomainInContext } from '@atlaskit/atlassian-context/get-domain-in-context';
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';

import { fg } from '@confluence/feature-gating';
import { getValue } from '@confluence/lifted-promise';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';

export function getLoginLink(continueUrl?: string | ((identity: string) => string)): string {
	const identity = getIdentity();

	if (typeof continueUrl === 'function') {
		continueUrl = continueUrl(identity);
	}
	if (!continueUrl) {
		continueUrl = window.location.href; // eslint-disable-line no-restricted-syntax
	}

	return `https://${identity}/login?application=confluence&continue=${encodeURIComponent(
		continueUrl,
	)}`;
}

function getIdentity() {
	let identity: string | undefined;
	const DEFAULT_PRODUCTION = 'id.atlassian.com';
	const DEFAULT_STAGING = 'id.stg.internal.atlassian.com';

	// 1. A branch build can be used in both staging and production instances so
	//    attempt to decide based on the frontend runtime environment rather than
	//    the frontend build environment:
	if (process.env.CLOUD_ENV === 'branch') {
		const { hostname } = window.location;
		if (hostname.endsWith('jira-dev.com')) {
			identity = DEFAULT_STAGING;
		} else if (hostname.endsWith('atlassian.net')) {
			identity = DEFAULT_PRODUCTION;
		}
	}

	// 2. Fall back to the frontend build environment:
	if (!identity) {
		identity = process.env.IDENTITY_PLATFORM;
	}

	if (!identity && isFedRamp()) {
		const isStagingFed =
			process.env.CLOUD_ENV === 'staging' ||
			process.env.CLOUD_ENV === 'stg-fedm' ||
			process.env.CLOUD_ENV === 'branch-fedm';
		identity = isStagingFed ? 'id.stg.atlassian-us-gov-mod.com' : 'id.atlassian-us-gov-mod.com';
	}

	// 3. Fall back to the backend runtime environment:
	if (!identity) {
		// Turning getLoginLink into a hook would add the complexity of skipability.
		// Given that (a) we're implementing a fallback here and (b) the frontend
		// app figures out whether login is necessary by looking at session data,
		// the backend runtime environment will very likely be known already.
		const environment = getValue(getSessionData())?.environment;
		identity = environment === 'PRODUCTION' ? DEFAULT_PRODUCTION : DEFAULT_STAGING;
	}

	if (!isIsolatedCloud() || !fg('confluence_ic_custom_domain_links')) {
		return identity;
	}

	// IC domains are derived from runtime perimeter context; the environment
	// argument only affects non-IC resolution.
	return getDomainInContext('id', 'prod') ?? identity;
}
